import React, { useState } from 'react';
import { View, Text, Pressable, Platform } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useConsole, TITLES } from '../context/voiceConsoleContext';
import { useSession } from '../context/sessionContext';
import Icon from '../components/icon';

// Two-letter avatar mark from the real signed-in user (name → initials, else
// email → first two letters), falling back to a role-generic mark.
function userMarkFrom(fullName: string, email: string | undefined, fallback: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  if (parts.length === 1 && parts[0]) return parts[0].slice(0, 2).toUpperCase();
  if (email) return email.slice(0, 2).toUpperCase();
  return fallback;
}

// Topbar — pixel-faithful to the HTML <header> (56px, paper bg, bottom border).
// Title + subtitle · tenant switcher (operator/viewer) OR "Global · all orgs"
// pill (softknack) · theme toggle · avatar.
export default function VoiceTopbar({ onSignOut }: { onSignOut?: () => void }) {
  const { theme, isDark, toggleTheme } = useTheme();
  const c = theme.colors;
  const { role, screen, tenant, sidebarCollapsed, toggleSidebar } = useConsole();
  const session = useSession();

  const [title, sub] = TITLES[screen];
  const screenSub = role === 'softknack' ? sub : `${sub} · ${tenant.name}`;
  const isSoftknack = role === 'softknack';
  const userMark = userMarkFrom(session.fullName, session.user?.email, '—');
  const email = session.user?.email ?? '';

  // Avatar opens a small account menu (email + Log out). Open on click,
  // stays open until an outside click or Log out — Plivo-style, minimal.
  const [menuOpen, setMenuOpen] = useState(false);
  const isWeb = Platform.OS === 'web';

  return (
    <View style={{ height: 56, flexShrink: 0, flexDirection: 'row', alignItems: 'center', gap: 14, paddingHorizontal: 22, borderBottomWidth: 1, borderBottomColor: c.line, backgroundColor: c.paper,
      // Web: stack above the scrollable content and let the account dropdown
      // overflow past the 56px bar instead of being clipped.
      ...(isWeb ? ({ position: 'relative', zIndex: 100, overflow: 'visible' } as any) : {}) }}>
      {/* Hamburger — only when the sidebar is collapsed, to reopen it. */}
      {sidebarCollapsed && (
        <Pressable onPress={toggleSidebar} accessibilityLabel="Open sidebar"
          style={{ width: 34, height: 34, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: c.line, borderRadius: 7, backgroundColor: c.card }}>
          <Icon name="menu" size={17} color={c.ink} />
        </Pressable>
      )}
      <View style={{ minWidth: 0, flexShrink: 1 }}>
        <Text numberOfLines={1} style={{ fontWeight: '600', fontSize: 15, letterSpacing: -0.1, color: c.ink, fontFamily: theme.font.sans }}>{title}</Text>
        <Text numberOfLines={1} style={{ fontSize: 11, color: c.ink3, fontFamily: theme.font.sans }}>{screenSub}</Text>
      </View>
      <View style={{ flex: 1 }} />

      {/* Static org identity — the real signed-in org (no switcher/impersonation). */}
      {!isSoftknack && (
        <View
          style={{ flexDirection: 'row', alignItems: 'center', gap: 8, height: 34, paddingHorizontal: 12, borderWidth: 1, borderColor: c.line, borderRadius: 7, backgroundColor: c.card }}>
          <View style={{ width: 20, height: 20, borderRadius: 5, backgroundColor: c.paper3, borderWidth: 1, borderColor: c.line, alignItems: 'center', justifyContent: 'center' }}>
            <Text style={{ fontSize: 10, fontWeight: '700', color: c.ink, fontFamily: theme.font.sans }}>{tenant.mark}</Text>
          </View>
          <Text style={{ fontWeight: '500', fontSize: 12, color: c.ink, fontFamily: theme.font.sans }}>{tenant.name}</Text>
        </View>
      )}

      {isSoftknack && (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7, height: 34, paddingHorizontal: 12, borderWidth: 1, borderColor: c.line, borderRadius: 7, backgroundColor: c.paper2 }}>
          <Icon name="v-globe" size={14} color={c.ink3} />
          <Text style={{ fontSize: 12, fontWeight: '500', color: c.ink3, fontFamily: theme.font.sans }}>Global · all orgs</Text>
        </View>
      )}

      <Pressable onPress={toggleTheme}
        style={{ width: 34, height: 34, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: c.line, borderRadius: 7, backgroundColor: c.card }}>
        <Icon name={isDark ? 'v-sun' : 'v-moon'} size={16} color={c.ink} />
      </Pressable>

      {/* Avatar → account menu (email + Log out). A bare click no longer
          signs out; logout is now an explicit item in the dropdown. */}
      <View style={{ position: 'relative' }}>
        <Pressable
          onPress={() => setMenuOpen((o) => !o)}
          accessibilityRole="button"
          accessibilityLabel="Account menu"
          style={{ width: 32, height: 32, borderRadius: 999, backgroundColor: c.ink, alignItems: 'center', justifyContent: 'center', ...(isWeb ? ({ cursor: 'pointer' } as any) : {}) }}>
          <Text style={{ color: c.paper, fontWeight: '600', fontSize: 12, fontFamily: theme.font.sans }}>{userMark}</Text>
        </Pressable>

        {menuOpen && (
          <>
            {/* Outside-click catcher. Web: position:fixed + inset:0 covers the
                viewport WITHOUT growing the document (an absolute ±2000 box
                expands layout → stray scroll/blank page). Native: full-parent. */}
            <Pressable
              onPress={() => setMenuOpen(false)}
              style={(isWeb
                ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 90 }
                : { position: 'absolute', top: -1000, left: -1000, right: -1000, bottom: -1000, zIndex: 90 }) as any}
            />
            <View
              style={{
                position: 'absolute', top: 40, right: 0, minWidth: 220,
                backgroundColor: c.card, borderWidth: 1, borderColor: c.line, borderRadius: 10,
                paddingVertical: 6, zIndex: 100,
                ...(isWeb ? ({ boxShadow: '0 8px 24px rgba(0,0,0,0.12)' } as any) : { elevation: 8 }),
              }}>
              {!!email && (
                <View style={{ paddingHorizontal: 14, paddingTop: 8, paddingBottom: 10 }}>
                  <Text numberOfLines={1} style={{ fontSize: 13, color: c.ink, fontWeight: '500', fontFamily: theme.font.sans }}>{email}</Text>
                </View>
              )}
              {!!email && <View style={{ height: 1, backgroundColor: c.line, marginBottom: 4 }} />}
              <Pressable
                onPress={() => { setMenuOpen(false); onSignOut?.(); }}
                disabled={!onSignOut}
                accessibilityRole="button"
                accessibilityLabel="Log out"
                style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 9, paddingHorizontal: 14, ...(isWeb ? ({ cursor: 'pointer' } as any) : {}) }}>
                <Icon name="logout" size={15} color={c.ink} />
                <Text style={{ fontSize: 13, fontWeight: '500', color: c.ink, fontFamily: theme.font.sans }}>Log out</Text>
              </Pressable>
            </View>
          </>
        )}
      </View>
    </View>
  );
}
